import apiClient from './api';

// Reason: FastAPI validation errors (422) send `detail` as an ARRAY of error
// objects, not a string — passing it straight to Error() renders
// "[object Object]" in the UI. Only a string detail is a usable message.
const detailMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};

export const getLatestBriefing = async () => {
  try {
    const response = await apiClient.get('/api/daily-briefing');
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('No briefing available');
    }
    throw new Error(detailMessage(error, 'Failed to fetch daily briefing'));
  }
};

export const getLatestBriefingConditional = async ({ etag } = {}) => {
  try {
    const config = {
      validateStatus: s => s === 304 || (s >= 200 && s < 300),
      ...(etag && { headers: { 'If-None-Match': etag } }),
    };
    const response = await apiClient.get('/api/daily-briefing', config);
    if (response.status === 304) {
      return { status: 304, etag, payload: null };
    }
    return {
      status: 200,
      etag: response.headers['etag'] ?? null,
      payload: response.data,
    };
  } catch (error) {
    // Reason: 404 = no briefing exists yet — maps to null payload, not an error state.
    if (error.response?.status === 404) {
      return { status: 200, etag: null, payload: null };
    }
    throw new Error(detailMessage(error, 'Failed to fetch daily briefing'));
  }
};

export const getBriefingDates = async ({ etag, limit } = {}) => {
  try {
    const config = {
      validateStatus: s => s === 304 || (s >= 200 && s < 300),
      // Reason: an explicit nullish check, not `limit &&` — `limit: 0` is a
      // caller bug the backend answers with 422, and swallowing it would return
      // a default-30 response as though the caller had asked for one.
      ...(limit != null && { params: { limit } }),
      ...(etag && { headers: { 'If-None-Match': etag } }),
    };
    const response = await apiClient.get('/api/daily-briefing/dates', config);
    if (response.status === 304) {
      return { status: 304, etag, payload: null };
    }
    return {
      status: 200,
      etag: response.headers['etag'] ?? null,
      payload: response.data,
    };
  } catch (error) {
    // Reason: 404 = no catalogue to serve — maps to an empty catalogue, not an
    // error state (§4). The shape must stay a full envelope: the caller reads
    // `current_date` off it, and a bare null payload would strand that field and
    // silently hand the day boundary back to the browser clock.
    if (error.response?.status === 404) {
      return { status: 200, etag: null, payload: { current_date: null, dates: [] } };
    }
    throw new Error(detailMessage(error, 'Failed to fetch briefing dates'));
  }
};

export const getBriefingByDateConditional = async (date, { etag } = {}) => {
  try {
    const config = {
      validateStatus: s => s === 304 || (s >= 200 && s < 300),
      ...(etag && { headers: { 'If-None-Match': etag } }),
    };
    const response = await apiClient.get(`/api/daily-briefing/${date}`, config);
    if (response.status === 304) {
      return { status: 304, etag, payload: null };
    }
    return {
      status: 200,
      etag: response.headers['etag'] ?? null,
      payload: response.data,
    };
  } catch (error) {
    // Reason: 404 = no completed briefing for that date — maps to null payload, not an error state.
    if (error.response?.status === 404) {
      return { status: 200, etag: null, payload: null };
    }
    throw new Error(detailMessage(error, 'Failed to fetch daily briefing'));
  }
};

export const generateBriefing = async ({ force = false } = {}) => {
  try {
    const response = await apiClient.post('/api/daily-briefing/generate', null, {
      params: force ? { force: true } : {},
    });
    return response.data;
  } catch (error) {
    throw new Error(detailMessage(error, 'Failed to generate briefing'));
  }
};

export const saveItem = async (briefingId, themeIndex, section, itemIndex) => {
  try {
    const response = await apiClient.post(
      `/api/daily-briefing/${briefingId}/save-item`,
      { theme_index: themeIndex, section, item_index: itemIndex }
    );
    return response.data;
  } catch (error) {
    throw new Error(detailMessage(error, 'Failed to save item'));
  }
};
